import React from 'react';
import {
  DndContext,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import TimelineRow from '../components/TimelineRow';
import TimelineBlock from '../components/TimelineBlock';

const HOURS_VISIBLE = 24;

// Gantt-style horizontal timeline (T7.2). Dragging a block computes the new
// start hour from the horizontal pixel delta over the dragged row's lane
// width, then calls onReschedule(jobId, newStartHour) — the page applies it
// optimistically and rolls back if the API rejects.
function TimelineView({ jobs, onEditJob, onReschedule }) {
  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: { distance: 6 }
  }));

  const handleDragEnd = (event) => {
    const { active, delta } = event;

    if (!delta?.x || Math.abs(delta.x) < 8) {
      return;
    }

    const job = jobs.find((entry) => entry.id === active.id);

    if (!job) {
      return;
    }

    const lane = document.querySelector(`[data-lane="${job.id}"]`);
    const laneWidth = lane?.getBoundingClientRect().width || 0;

    if (!laneWidth) {
      return;
    }

    const hourWidth = laneWidth / HOURS_VISIBLE;
    const newStartHour = Math.max(0, Math.min(
      HOURS_VISIBLE - job.durationHours,
      Math.round(job.startHour + delta.x / hourWidth)
    ));

    if (newStartHour !== job.startHour) {
      onReschedule(job.id, newStartHour);
    }
  };

  return (
    <DndContext
      collisionDetection={pointerWithin}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      <div className="timeline-card">
        <div className="timeline-header">
          <div className="timeline-gutter" />
          <div className="timeline-hours">
            {Array.from({ length: HOURS_VISIBLE }, (_, hour) => (
              <span key={hour}>{String(hour).padStart(2, '0')}</span>
            ))}
          </div>
        </div>
        <SortableContext
          items={jobs.map((job) => job.id)}
          strategy={horizontalListSortingStrategy}
        >
          <div className="timeline-rows">
            {jobs.map((job) => (
              <TimelineRow job={job} key={job.id}>
                <TimelineBlock
                  hoursVisible={HOURS_VISIBLE}
                  job={job}
                  onEdit={() => onEditJob?.(job.id)}
                />
              </TimelineRow>
            ))}
          </div>
        </SortableContext>
      </div>
    </DndContext>
  );
}

export default TimelineView;
